import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import useEmailVerification from '../../../hooks/useEmailVerification';
import rootAxios from 'requests/rootAxios';
import trainingTypes from 'Constants/trainingTypes';
const EmailChip = styled(Chip)(({ theme }) => ({
  margin: '2px',
  '& .MuiChip-deleteIcon': {
    color: theme.palette.error.main,
  },
}));

const EmailModalCollection = ({
  open,
  onClose,
  onSubmit,
  initialEmailConfig,
  regions,
  trainingType,
  selectedEmployees,
  pendencyData,
  trainingTypeKey,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [emailData, setEmailData] = useState({
    stage1: { to: [], cc: [] },
    stage2: { to: [], cc: [] },
  });
  const [currentToEmail, setCurrentToEmail] = useState('');
  const [currentCCEmail, setCurrentCCEmail] = useState('');
  const [verificationResults, setVerificationResults] = useState({
    stage1: null,
    stage2: null,
  });
  const [refreshLoading, setRefreshLoading] = useState(false);
  const userData = useSelector((state) => state.authSlice.userData);

  // Use the reusable email verification hook
  const { verifyEmails, loading, verified, error } = useEmailVerification();
  console.log('initialEmailConfig', initialEmailConfig);

  // Ref to track previous employee selection for comparison
  const prevEmployeeSelection = useRef(null);

  // Function to get unique regions from selected employees
  const getSelectedEmployeeRegions = () => {
    if (!selectedEmployees || !pendencyData || !trainingTypeKey) return [];

    const selectedEmployeeIds = selectedEmployees[trainingTypeKey] || [];
    const employees = pendencyData[trainingTypeKey] || [];

    // Get unique regions from selected employees with both regionId and regionName
    const uniqueRegions = [
      ...new Set(
        employees
          .filter((emp) => selectedEmployeeIds.includes(emp.employeeId))
          .map((emp) => emp.regionId)
          .filter(Boolean)
      ),
    ];

    // Convert region IDs to region objects that match the expected format
    return uniqueRegions.map((regionId) => {
      // Find the corresponding regionName for this regionId
      const employeeWithRegion = employees.find(
        (emp) => selectedEmployeeIds.includes(emp.employeeId) && emp.regionId === regionId
      );

      return {
        regionId: regionId,
        regionName: employeeWithRegion?.regionName || regionId,
      };
    });
  };

  // if initialEmailConfig is not provided, then hit api and send regions , trainingType , RM, CH as paylaod to get "TO" emails.
  useEffect(() => {
    if (initialEmailConfig?.steps?.[0]?.to?.length === 0 && trainingType) {
      const selectedRegions = getSelectedEmployeeRegions();

      if (selectedRegions.length === 0) {
        console.log('No regions found for selected employees');
        return;
      }

      const fetchEmailData = async () => {
        try {
          setRefreshLoading(true);
          console.log('Fetching email data for regions:', selectedRegions);

          // Both stages are for CH in collection training
          const response = await rootAxios.post('/user/get-training-auth-emails', {
            regions: selectedRegions?.map((region) => region?.regionId) || [],
            trainingType: trainingType,
            type: trainingType.toLowerCase() === trainingTypes.CORRECTIVE_TRAINING ? 'CL' : 'ch',
          });

          console.log('CH response:', response?.data?.data);

          // Set emails for both stages (both use circleEmails)
          setEmailData((prev) => ({
            ...prev,
            stage1: {
              ...prev.stage1,
              to: response?.data?.data?.circleEmails || [],
            },
            stage2: {
              ...prev.stage2,
              to: response?.data?.data?.circleEmails || [],
            },
          }));
        } catch (error) {
          console.error('Error fetching email data:', error);
        } finally {
          setRefreshLoading(false);
        }
      };
      fetchEmailData();
    }
  }, [initialEmailConfig, trainingType, selectedEmployees, pendencyData, trainingTypeKey]);

  // Refresh email data when selected employees change
  useEffect(() => {
    if (open && trainingType && selectedEmployees && pendencyData && trainingTypeKey) {
      const selectedRegions = getSelectedEmployeeRegions();

      if (selectedRegions.length === 0) {
        console.log('No regions found for selected employees, clearing email data');
        setEmailData({
          stage1: { to: [], cc: [] },
          stage2: { to: [], cc: [] },
        });
        // Clear verification results when clearing email data
        setVerificationResults({
          stage1: null,
          stage2: null,
        });
        return;
      }

      // Only refresh if we don't already have email data
      if (emailData.stage1.to.length === 0) {
        console.log('Refreshing email data for new employee selection');
        const fetchEmailData = async () => {
          try {
            setRefreshLoading(true);
            console.log('Fetching email data for regions:', selectedRegions);

            // Both stages are for CH in collection training
            const response = await rootAxios.post('/user/get-training-auth-emails', {
              regions: selectedRegions?.map((region) => region?.regionId) || [],
              trainingType: trainingType,
              type: trainingType === trainingTypes.CORRECTIVE_TRAINING ? 'CL' : 'ch',
            });

            console.log('CH response:', response?.data?.data);

            // Set emails for both stages (both use circleEmails)
            setEmailData((prev) => ({
              ...prev,
              stage1: {
                ...prev.stage1,
                to: response?.data?.data?.circleEmails || [],
              },
              stage2: {
                ...prev.stage2,
                to: response?.data?.data?.circleEmails || [],
              },
            }));
          } catch (error) {
            console.error('Error fetching email data:', error);
          } finally {
            setRefreshLoading(false);
          }
        };
        fetchEmailData();
      }
    }
  }, [
    selectedEmployees,
    pendencyData,
    trainingTypeKey,
    open,
    trainingType,
    emailData.stage1.to.length,
  ]);

  // Detect significant changes in employee selection and clear email data
  useEffect(() => {
    if (open && selectedEmployees && trainingTypeKey) {
      const currentSelection = selectedEmployees[trainingTypeKey] || [];
      const prevSelection = prevEmployeeSelection.current;

      // Check if employee selection changed significantly
      if (
        prevSelection &&
        (currentSelection.length !== prevSelection.length ||
          !currentSelection.every((id) => prevSelection.includes(id)))
      ) {
        console.log('Employee selection changed significantly, clearing email data');
        setEmailData({
          stage1: { to: [], cc: [] },
          stage2: { to: [], cc: [] },
        });
        setVerificationResults({
          stage1: null,
          stage2: null,
        });
      }

      // Update the ref with current selection
      prevEmployeeSelection.current = currentSelection;
    }
  }, [selectedEmployees, trainingTypeKey, open]);

  // Clear email data when modal opens to ensure fresh data
  // But don't clear if we have initialEmailConfig (edit mode)
  useEffect(() => {
    if (open && !(initialEmailConfig?.steps && initialEmailConfig.steps.length > 0)) {
      setEmailData({
        stage1: { to: [], cc: [] },
        stage2: { to: [], cc: [] },
      });
      setVerificationResults({
        stage1: null,
        stage2: null,
      });
      setActiveStep(0);
    } else if (open) {
      // In edit mode, just reset the step
      setActiveStep(0);
    }
  }, [open, initialEmailConfig]);

  // Prefill email data when initialEmailConfig is provided
  useEffect(() => {
    if (initialEmailConfig?.steps && open) {
      const transformedData = {
        stage1: {
          to: initialEmailConfig.steps[0]?.to || [],
          cc: (initialEmailConfig.steps[0]?.cc || []).includes(userData?.officalEmail)
            ? initialEmailConfig.steps[0]?.cc || []
            : [...(initialEmailConfig.steps[0]?.cc || []), userData?.officalEmail],
        },
        stage2: {
          to: initialEmailConfig.steps[1]?.to || [],
          cc: (initialEmailConfig.steps[1]?.cc || []).includes(userData?.officalEmail)
            ? initialEmailConfig.steps[1]?.cc || []
            : [...(initialEmailConfig.steps[1]?.cc || []), userData?.officalEmail],
        },
      };
      setEmailData(transformedData);
    }
  }, [initialEmailConfig, open, userData?.officalEmail]);

  const handleNext = () => {
    if (activeStep < 1) {
      // Copy CC emails from current step to next step
      const currentStage = `stage${activeStep + 1}`;
      const nextStage = `stage${activeStep + 2}`;
      const currentCCEmails = emailData[currentStage]?.cc || [];

      if (currentCCEmails.length > 0) {
        // Show notification that CC emails are being carried forward
        console.log(`Carrying forward ${currentCCEmails.length} CC email(s) to next step`);
      }

      setEmailData((prev) => ({
        ...prev,
        [nextStage]: {
          ...prev[nextStage],
          cc: [...currentCCEmails], // Copy CC emails to next step
        },
      }));

      setActiveStep((prevStep) => prevStep + 1);
    } else {
      onSubmit(emailData);
      onClose();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleEmailKeyDown = (stage, field, e, currentEmail, setCurrentEmail) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const email = currentEmail?.trim();
      if (email) {
        const emails = email
          .split(',')
          .map((e) => e.trim())
          .filter((e) => e && isValidEmail(e));
        if (emails.length > 0) {
          setEmailData((prev) => ({
            ...prev,
            [stage]: {
              ...prev[stage],
              [field]: [...(prev[stage]?.[field] || []), ...emails],
            },
          }));
          setCurrentEmail('');
        }
      }
    }
  };

  const handlePaste = (stage, field, e, setCurrentEmail) => {
    e.preventDefault();
    const pastedText = e.clipboardData?.getData('text') || '';
    if (pastedText) {
      const emails = pastedText
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e && isValidEmail(e));

      if (emails.length > 0) {
        setEmailData((prev) => ({
          ...prev,
          [stage]: {
            ...prev[stage],
            [field]: [...(prev[stage]?.[field] || []), ...emails],
          },
        }));
        setCurrentEmail('');
      }
    }
  };

  const handleDeleteEmail = (stage, field, emailToDelete) => {
    setEmailData((prev) => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        [field]: (prev[stage]?.[field] || []).filter((email) => email !== emailToDelete),
      },
    }));
    // Reset verification results when emails are modified
    setVerificationResults((prev) => ({
      ...prev,
      [stage]: null,
    }));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleVerifyEmails = async (stage) => {
    if ((emailData[stage]?.to || []).length === 0) {
      return;
    }

    try {
      let verifyEmailsResponse = await verifyEmails(
        emailData[stage]?.to || [],
        trainingType === trainingTypes.CORRECTIVE_TRAINING ? 'CL' : 'ch'
      );
      let verifiedResult = verifyEmailsResponse.data;

      console.log('verify email', verifiedResult);

      // Store verification results
      setVerificationResults((prev) => ({
        ...prev,
        [stage]: verifiedResult,
      }));
    } catch (error) {
      // Error is already handled by the hook
      console.error('Email verification failed:', error);
    }
  };

  const handleRefreshEmails = async () => {
    const selectedRegions = getSelectedEmployeeRegions();

    if (selectedRegions.length === 0) {
      console.log('No regions found for selected employees');
      return;
    }

    try {
      setRefreshLoading(true);
      console.log('Refreshing email data for regions:', selectedRegions);

      // Both stages are for CH in collection training
      const response = await rootAxios.post('/user/get-training-auth-emails', {
        regions: selectedRegions?.map((region) => region?.regionId) || [],
        trainingType: trainingType,
        type: trainingType === trainingTypes.CORRECTIVE_TRAINING ? 'CL' : 'ch',
      });

      console.log('CH response:', response?.data?.data);

      // Set emails for both stages (both use circleEmails)
      setEmailData((prev) => ({
        ...prev,
        stage1: {
          ...prev.stage1,
          to: response?.data?.data?.circleEmails || [],
        },
        stage2: {
          ...prev.stage2,
          to: response?.data?.data?.circleEmails || [],
        },
      }));

      // Clear verification results since we have new emails
      setVerificationResults({
        stage1: null,
        stage2: null,
      });
    } catch (error) {
      console.error('Error refreshing email data:', error);
    } finally {
      setRefreshLoading(false);
    }
  };

  const isStageVerified = (stage) => {
    const results = verificationResults[stage];
    if (!results || !Array.isArray(results)) return false;

    // Check if all emails are verified
    return results.every((result) => result.verified === true);
  };

  const getUnverifiedEmails = (stage) => {
    const results = verificationResults[stage];
    if (!results || !Array.isArray(results)) return [];

    return results.filter((result) => !result.verified).map((result) => result.email);
  };

  const getStepContent = (step) => {
    const stage = `stage${step + 1}`;
    const isLastStep = step === 1; // stage2 is the last step
    const isVerified = isStageVerified(stage);
    const unverifiedEmails = getUnverifiedEmails(stage);

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Stage {step + 1} Email Details
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Typography variant="subtitle2" gutterBottom>
              To Emails
            </Typography>
            {!isLastStep && verificationResults[stage] && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isVerified ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <ErrorIcon color="error" fontSize="small" />
                )}
                <Typography variant="caption" color={isVerified ? 'success' : 'error'}>
                  {isVerified
                    ? 'All emails verified'
                    : `Not verified: ${unverifiedEmails.join(', ')}`}
                </Typography>
              </Box>
            )}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(emailData[stage]?.to || []).map((email, index) => {
              const verificationResult = verificationResults[stage]?.find(
                (result) => result.email.toLowerCase() === email.toLowerCase()
              );
              return (
                <EmailChip
                  key={index}
                  label={email}
                  onDelete={() => handleDeleteEmail(stage, 'to', email)}
                  color={verificationResult?.verified ? 'success' : 'default'}
                  variant={verificationResult?.verified ? 'filled' : 'outlined'}
                />
              );
            })}
          </Stack>
          <TextField
            fullWidth
            label="Add To Email"
            value={currentToEmail}
            onChange={(e) => setCurrentToEmail(e.target.value)}
            onKeyDown={(e) => handleEmailKeyDown(stage, 'to', e, currentToEmail, setCurrentToEmail)}
            onPaste={(e) => handlePaste(stage, 'to', e, setCurrentToEmail)}
            margin="normal"
            type="email"
            helperText="Press space, enter, or paste comma-separated emails"
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Typography variant="subtitle2" gutterBottom>
              CC Emails
            </Typography>
            {step > 0 && (emailData[stage]?.cc || []).length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setEmailData((prev) => ({
                    ...prev,
                    [stage]: {
                      ...prev[stage],
                      cc: [],
                    },
                  }));
                }}
                sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
              >
                Clear All
              </Button>
            )}
          </Box>
          {step === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              CC emails you add here will be carried forward to the next steps automatically.
            </Typography>
          )}
          {step > 0 && (emailData[stage]?.cc || []).length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              CC emails from previous step are automatically carried forward. You can modify or add
              new ones.
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(emailData[stage]?.cc || []).map((email, index) => (
              <EmailChip
                key={index}
                label={email}
                onDelete={() => handleDeleteEmail(stage, 'cc', email)}
              />
            ))}
          </Stack>
          <TextField
            fullWidth
            label="Add CC Email"
            value={currentCCEmail}
            onChange={(e) => setCurrentCCEmail(e.target.value)}
            onKeyDown={(e) => handleEmailKeyDown(stage, 'cc', e, currentCCEmail, setCurrentCCEmail)}
            onPaste={(e) => handlePaste(stage, 'cc', e, setCurrentCCEmail)}
            margin="normal"
            type="email"
            helperText="Press space, enter, or paste comma-separated emails"
          />
        </Box>
      </Box>
    );
  };

  const currentStage = `stage${activeStep + 1}`;
  const isCurrentStageVerified = isStageVerified(currentStage);
  const isLastStep = activeStep === 1; // stage2 is the last step
  const showVerifyButton =
    !isLastStep && (emailData[currentStage]?.to || []).length > 0 && !isCurrentStageVerified;
  const showNextButton = isLastStep || isCurrentStageVerified;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Email Configuration</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          <Step>
            <StepLabel>For CH Approval</StepLabel>
          </Step>
          <Step>
            <StepLabel>Training Invitation</StepLabel>
          </Step>
        </Stepper>

        {/* Display selected regions info */}
        {(() => {
          const selectedRegions = getSelectedEmployeeRegions();
          const selectedEmployeeCount = selectedEmployees?.[trainingTypeKey]?.length || 0;

          // In edit mode (with initialEmailConfig but no selectedEmployees), show edit mode message
          if (
            initialEmailConfig?.steps &&
            initialEmailConfig.steps.length > 0 &&
            (!selectedEmployees || !selectedEmployees[trainingTypeKey]?.length)
          ) {
            return (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="info.contrastText" sx={{ mb: 1 }}>
                  Editing Email Configuration
                </Typography>
                <Typography variant="body2" color="info.contrastText">
                  You can modify the email recipients for this batch.
                </Typography>
              </Box>
            );
          }

          if (selectedRegions.length > 0) {
            return (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="info.contrastText" sx={{ mb: 1 }}>
                  Email Configuration for Selected Employee Regions:
                </Typography>
                <Typography variant="body2" color="info.contrastText" sx={{ mb: 1 }}>
                  {selectedRegions.map((region) => region.regionName).join(', ')}
                </Typography>
                <Typography variant="caption" color="info.contrastText">
                  Total Selected Employees: {selectedEmployeeCount}
                </Typography>
              </Box>
            );
          } else if (selectedEmployees && trainingTypeKey && selectedEmployeeCount === 0) {
            return (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="warning.contrastText" sx={{ mb: 1 }}>
                  No Employees Selected
                </Typography>
                <Typography variant="body2" color="warning.contrastText">
                  Please select employees first to configure email recipients.
                </Typography>
              </Box>
            );
          }
          return null;
        })()}

        {/* Allow form if employees are selected OR if we have initialEmailConfig (edit mode) */}
        {selectedEmployees?.[trainingTypeKey]?.length > 0 ||
        (initialEmailConfig?.steps && initialEmailConfig.steps.length > 0) ? (
          (() => {
            // In edit mode (with initialEmailConfig), skip region check
            if (
              initialEmailConfig?.steps &&
              initialEmailConfig.steps.length > 0 &&
              (!selectedEmployees || !selectedEmployees[trainingTypeKey]?.length)
            ) {
              return getStepContent(activeStep);
            }

            const selectedRegions = getSelectedEmployeeRegions();
            if (selectedRegions.length === 0) {
              return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                    No Regions Found
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    No regions found for the selected employees. Please check employee data or
                    contact administrator.
                  </Typography>
                </Box>
              );
            }
            return getStepContent(activeStep);
          })()
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
              No Employees Selected
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Please go back and select employees before configuring email settings.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleRefreshEmails}
          variant="outlined"
          color="primary"
          disabled={
            loading ||
            refreshLoading ||
            (!selectedEmployees?.[trainingTypeKey]?.length &&
              !(initialEmailConfig?.steps && initialEmailConfig.steps.length > 0))
          }
        >
          {refreshLoading ? 'Refreshing...' : 'Refresh Emails'}
        </Button>
        <Button onClick={handleBack} disabled={activeStep === 0}>
          Back
        </Button>
        {showVerifyButton && (
          <Button
            onClick={() => handleVerifyEmails(currentStage)}
            variant="contained"
            disabled={
              loading ||
              (!selectedEmployees?.[trainingTypeKey]?.length &&
                !(initialEmailConfig?.steps && initialEmailConfig.steps.length > 0))
            }
            color="primary"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        )}
        {showNextButton && (
          <>
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={
                !(emailData[currentStage]?.to || []).length ||
                (!selectedEmployees?.[trainingTypeKey]?.length &&
                  !(initialEmailConfig?.steps && initialEmailConfig.steps.length > 0))
              }
            >
              {activeStep === 1 ? 'Finish' : 'Next'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmailModalCollection;
